import { type LayoutConfiguration } from '@/types/layout-configuration';
import dynamic from 'next/dynamic';
import { type ReactNode } from 'react';

/**
 * Both layouts load on demand. `ScribeLayout` drags in the whole app shell —
 * the resizable layout, the settings sidebar, the tutorial panel, the dictation
 * bubble — and `_app.tsx` is on every route, including the auxiliary Tauri
 * windows that deliberately render a minimal tree. A static import here would
 * put the shell in their bundles to serve a static none of them sets.
 *
 * Server rendering is left on (the default), so a page that declares a layout
 * still prerenders its chrome the way the pages importing `ScribeLayout`
 * directly do today.
 */
const ScribeLayout = dynamic(
  () => import('@/components/layout/scribe-layout').then((mod) => mod.ScribeLayout),
  { loading: () => null }
);

const TablePageLayout = dynamic(
  () =>
    import('@/components/layout/table-page-layout').then((mod) => mod.TablePageLayout),
  { loading: () => null }
);

/**
 * Applies the page's declared `layout` static.
 *
 * Renders `children` untouched when no layout is declared, which is every page
 * today — the shape of the tree is unchanged and no layout chunk is requested.
 */
export const PageLayoutSlot = ({
  configuration,
  children,
}: {
  configuration: LayoutConfiguration | undefined;
  children: ReactNode;
}) => {
  const layout = configuration?.layout ?? 'none';
  if (layout === 'none') return <>{children}</>;

  return (
    <ScribeLayout {...configuration?.layoutProps}>
      {layout === 'table' ? <TablePageLayout>{children}</TablePageLayout> : children}
    </ScribeLayout>
  );
};
